import * as React from 'react';
import { Video } from 'lucide-react';
import { cn } from '../../utils';
import { Card, CardDescription, CardHeader, CardTitle } from '../../components/core/card';
import { Skeleton } from '../../components/core/skeleton';

export type HeroCardLink = {
  to: string;
  params?: Record<string, string>;
};

export type HeroCardMeta = {
  statusLabel: React.ReactNode;
  statusValue: React.ReactNode;
  detail?: React.ReactNode;
};

export type HeroCardJoinAction = {
  href: string;
  state?: string;
  stateLabel?: string;
  icon?: React.ReactNode;
  ariaLabel?: string;
};

export type HeroCardDefinition = {
  key: string;
  title: React.ReactNode;
  value: React.ReactNode;
  description?: React.ReactNode;
  href?: HeroCardLink;
  onClick?: () => void;
  meta?: HeroCardMeta;
  join?: HeroCardJoinAction;
  className?: string;
};

export type HeroCardsLinkComponentProps = {
  to: string;
  params?: Record<string, string>;
  className?: string;
  children: React.ReactNode;
};

export type HeroCardsProps = {
  cards: HeroCardDefinition[];
  LinkComponent?: React.ComponentType<HeroCardsLinkComponentProps>;
  isLoading?: boolean;
  loadingCount?: number;
  className?: string;
};

const interactiveCardClass = [
  'h-full min-h-[10rem] text-white rounded-xl cursor-pointer',
  'bg-gradient-to-b from-white/20 to-white/10',
  'border border-white/30 border-b-[3px] border-b-white/50',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_4px_0_rgba(0,0,0,0.15),0_6px_12px_rgba(0,0,0,0.2)]',
  'transform transition-all duration-100 ease-out',
  'hover:translate-y-[2px] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_2px_0_rgba(0,0,0,0.15),0_3px_6px_rgba(0,0,0,0.18)]',
  'active:translate-y-[3px] active:shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_1px_0_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.15)]',
  'focus-visible:ring-2 focus-visible:ring-white/60',
].join(' ');

const defaultCardClass =
  'h-full min-h-[10rem] text-white border-none bg-white/15 rounded-xl transition hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white/60';

// Matches the settled p2e hero design (3 cards side by side) so every
// dashboard hero shares the same card size by default.
const baseContainerClass = 'grid w-full gap-3 grid-cols-1 sm:grid-cols-3';

function renderJoinAction(action: HeroCardJoinAction) {
  const icon = action.icon ?? <Video className="relative h-5 w-5 text-white/90" />;
  const stateLabel = action.stateLabel ?? action.state?.replace(/^\w/, (value) => value.toUpperCase());

  return (
    <div className="relative flex flex-col items-end gap-1.5 shrink-0 min-w-[52px]">
      <a
        href={action.href}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => {
          event.stopPropagation();
        }}
        className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white shadow-sm transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        aria-label={action.ariaLabel ?? 'Join event'}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 inline-flex h-full w-full animate-[ping_1.2s_ease-out_infinite] rounded-full bg-white/60 blur-[2px] drop-shadow-[0_0_12px_rgba(255,255,255,0.55)]"
        />
        {icon}
      </a>
      {stateLabel ? (
        <div className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white/90 backdrop-blur-sm text-center whitespace-nowrap">
          {action.state === 'live' ? stateLabel : 'Live'}
        </div>
      ) : null}
    </div>
  );
}

export function HeroCards({
  cards,
  LinkComponent,
  isLoading = false,
  loadingCount,
  className,
}: HeroCardsProps) {
  const skeletons = React.useMemo(
    () => (loadingCount ?? cards.length) ?? 0,
    [cards.length, loadingCount],
  );

  if (isLoading) {
    return (
      <div className={cn(baseContainerClass, className)}>
        {Array.from({ length: skeletons }).map((_, index) => (
          <Card key={`hero-skeleton-${index}`} className="min-h-[10rem] border-none bg-white/5 text-white">
            <CardHeader className="space-y-3">
              <Skeleton className="h-3 w-24 bg-white/30" />
              <Skeleton className="h-6 w-32 bg-white/40" />
              <Skeleton className="h-4 w-40 bg-white/20" />
            </CardHeader>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={cn(baseContainerClass, className)}>
      {cards.map((card) => {
        const isInteractive = card.href || card.onClick;
        const cardClass = cn(isInteractive ? interactiveCardClass : defaultCardClass, card.className);
        const cardContent = (
          <Card className={cardClass}>
            <CardHeader className="space-y-3 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <p className="flex-1 min-w-[50px] whitespace-normal break-words text-xs uppercase tracking-[0.3em] text-white/70">
                  {card.title}
                </p>
                {card.join ? renderJoinAction(card.join) : null}
              </div>
              <CardTitle className="text-2xl text-white">{card.value}</CardTitle>
              {card.description ? (
                <CardDescription className="text-white/80 break-words">{card.description}</CardDescription>
              ) : null}
              {card.meta ? (
                <div className="space-y-1 pt-3">
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-white/70">
                    <span>{card.meta.statusLabel}</span>
                    <span className="text-white">{card.meta.statusValue}</span>
                  </div>
                  {card.meta.detail ? (
                    <p className="text-xs text-white/70">{card.meta.detail}</p>
                  ) : null}
                </div>
              ) : null}
            </CardHeader>
          </Card>
        );

        if (card.href && LinkComponent) {
          return (
            <LinkComponent
              key={card.key}
              to={card.href.to}
              params={card.href.params}
              className="block focus:outline-none"
            >
              {cardContent}
            </LinkComponent>
          );
        }

        if (card.onClick) {
          return (
            <button
              key={card.key}
              type="button"
              className="block w-full text-left focus:outline-none"
              onClick={card.onClick}
            >
              {cardContent}
            </button>
          );
        }

        return (
          <div key={card.key}>
            {cardContent}
          </div>
        );
      })}
    </div>
  );
}
